import React from 'react';
import styled from 'styled-components';
import { theme, neutral, jadeGreen, garnetRed, goldenTopaz, lightBlue } from '../../design-system/theme';
import {
  ModalOverlay,
  ModalContent as SharedModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton as SharedCloseButton,
  PrimaryButton,
  // SecondaryButton and StatusBadge are available for direct use in consuming components
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  SecondaryButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  StatusBadge,
} from '../../styles/adminShared';

// ============================================================================
// MODAL EXTENSIONS (for custom width support)
// ============================================================================

const ModalContent = styled(SharedModalContent)<{ $width?: string }>`
  max-width: ${props => props.$width || '600px'};
`;

const CloseButton = styled(SharedCloseButton)``;

// ============================================================================
// CUSTOM SECTION COMPONENTS
// ============================================================================

const Section = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.primary};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, ${theme.primary} 0%, transparent 100%);
  }
`;

const InfoGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$columns || 2}, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div<{ $fullWidth?: boolean }>`
  background-color: ${neutral[1]};
  padding: 16px;
  border-radius: 8px;
  border-left: 3px solid ${theme.primary};
  grid-column: ${props => props.$fullWidth ? 'span 2' : 'auto'};
  
  @media (max-width: 768px) {
    grid-column: auto;
  }
`;

const InfoLabel = styled.div`
  font-size: 11px;
  color: ${theme.textSecondary};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
`;

const InfoValue = styled.div<{ $mono?: boolean }>`
  font-size: 14px;
  color: ${theme.textPrimary};
  word-break: break-word;
  font-family: ${props => props.$mono ? "'SF Mono', 'Monaco', monospace" : 'inherit'};
`;

const Badge = styled.span<{ $variant?: 'success' | 'warning' | 'danger' | 'info' | 'default' }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return jadeGreen[1];
      case 'warning': return goldenTopaz[1];
      case 'danger': return garnetRed[1];
      case 'info': return lightBlue[1];
      default: return neutral[2];
    }
  }};
  color: ${props => {
    switch (props.$variant) {
      case 'success': return jadeGreen[6];
      case 'warning': return goldenTopaz[8];
      case 'danger': return garnetRed[6];
      case 'info': return lightBlue[7];
      default: return theme.textSecondary;
    }
  }};
`;

// Button components are now imported from adminShared
// For backward compatibility with existing code, we can create a wrapper component
interface ButtonProps {
  $variant?: 'primary' | 'secondary' | 'danger';
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}

const StyledButton = styled(PrimaryButton)<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  ${props => {
    if (props.$variant === 'secondary') {
      return `
        background: ${neutral[2]};
        color: ${theme.textPrimary};
        &:hover:not(:disabled) {
          background: ${neutral[3]};
        }
      `;
    }
    if (props.$variant === 'danger') {
      return `
        background: ${garnetRed[5]};
        color: white;
        &:hover:not(:disabled) {
          background: ${garnetRed[7]};
        }
      `;
    }
    // Primary button styles are already applied from PrimaryButton
    return '';
  }}
`;

const Button = StyledButton as React.ComponentType<ButtonProps>;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.span`
  background: ${lightBlue[1]};
  color: ${lightBlue[7]};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
`;

interface ViewModalProps {
  show: boolean;
  onClose: () => void;
  title: string;
  icon?: string;
  width?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const ViewModal: React.FC<ViewModalProps> = ({
  show,
  onClose,
  title,
  icon,
  width,
  children,
  footer
}) => {
  if (!show) return null;
  
  return (
    <ModalOverlay $show={show} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent $width={width}>
        <ModalHeader>
          <ModalTitle>{icon && <span style={{ marginRight: '8px' }}>{icon}</span>}{title}</ModalTitle>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContent>
    </ModalOverlay>
  );
};

// Export sub-components for composition
ViewModal.Section = Section;
ViewModal.SectionTitle = SectionTitle;
ViewModal.InfoGrid = InfoGrid;
ViewModal.InfoCard = InfoCard;
ViewModal.InfoLabel = InfoLabel;
ViewModal.InfoValue = InfoValue;
ViewModal.Badge = Badge;
ViewModal.Button = Button;
ViewModal.TagList = TagList;
ViewModal.Tag = Tag;

// Type augmentation for sub-components
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface FunctionComponent<P = {}> {
    Section?: typeof Section;
    SectionTitle?: typeof SectionTitle;
    InfoGrid?: typeof InfoGrid;
    InfoCard?: typeof InfoCard;
    InfoLabel?: typeof InfoLabel;
    InfoValue?: typeof InfoValue;
    Badge?: typeof Badge;
    Button?: typeof Button;
    TagList?: typeof TagList;
    Tag?: typeof Tag;
  }
}

export { Section, SectionTitle, InfoGrid, InfoCard, InfoLabel, InfoValue, Badge, Button as ModalButton, TagList, Tag };
export default ViewModal;
